import { useMemo, type ReactNode } from 'react';
import { Link, useLocation } from 'wouter';
import { useQueryClient } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Shredder,
  FileSpreadsheet,
  FileCheck,
  ChartPie,
  PiggyBank,
  Banknote,
  Contact,
  Bell,
  UserCircle,
  ChevronsUpDown,
  LogOut,
  ArrowLeftRight,
  Contrast,
  type LucideIcon,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useTenantMe } from '@/hooks/use-tenant-me';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useTheme } from '@/components/theme-provider';
import { SupportWidget } from '@/components/support-widget';

interface AppShellProps {
  children: ReactNode;
}

interface NavItem {
  href: string;
  label: string;
  icon: LucideIcon;
  exact?: boolean;
}
interface NavSection {
  label: string;
  items: NavItem[];
}

// IVA y Renta viven bajo "Contabilidad" (son secciones del módulo contable),
// no en la navegación principal.
const navSections: NavSection[] = [
  {
    label: 'Navegación',
    items: [
      { href: '/app', label: 'Inicio', icon: LayoutDashboard, exact: true },
      { href: '/app/clientes', label: 'Clientes', icon: Contact },
      { href: '/app/facturas', label: 'Facturas', icon: Shredder },
      { href: '/app/presupuestos', label: 'Presupuestos', icon: FileSpreadsheet },
      { href: '/app/cobros', label: 'Cobros', icon: Banknote },
      { href: '/app/finanzas', label: 'Mis finanzas', icon: PiggyBank },
    ],
  },
  {
    label: 'Contabilidad',
    items: [
      { href: '/app/iva', label: 'Presentaciones IVA', icon: FileCheck },
      { href: '/app/renta', label: 'Resumen de Renta', icon: ChartPie },
    ],
  },
];

export function AppShell({ children }: AppShellProps) {
  return (
    <SidebarProvider>
      <TenantSidebar />
      <SidebarInset>
        {/* En mobile el sidebar es un sheet. El trigger va ABSOLUTO en el gutter
            superior-izq y SOLO el <h1> del título lleva padding-left para despejarlo
            → el texto del título arranca después del ícono y TODO lo demás (subtítulo,
            acciones, cards) queda flush a la izquierda. Funciona igual sea el header
            un div simple o un flex (apuntamos al h1, no al contenedor). En desktop el
            trigger se oculta y no hay padding. */}
        <div className="relative flex-1 px-2 py-4 md:p-6">
          <SidebarTrigger className="absolute left-2 top-4 z-10 md:hidden" />
          <div className="flex flex-col gap-4 max-md:[&>*:first-child_h1]:pl-9">{children}</div>
        </div>
      </SidebarInset>
      {/* Widget de soporte (webchat) — solo en el panel del tenant,
          identificado con los datos del contribuyente. No carga durante
          impersonation. */}
      <SupportWidget />
    </SidebarProvider>
  );
}

function TenantSidebar() {
  const [location] = useLocation();
  const { data: me } = useTenantMe();
  return (
    <Sidebar collapsible="icon" variant="inset">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link href="/app">
                <div className="flex aspect-square size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-white">
                  <img src="/actuo-icon.png" alt="Actuo" className="size-6 object-contain" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                  <span className="truncate font-semibold">Actuo</span>
                  <span className="truncate text-xs text-muted-foreground">
                    {me?.tenant.displayName ?? 'Mi cuenta'}
                  </span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {navSections.map((section) => (
          <SidebarGroup key={section.label}>
            <SidebarGroupLabel>{section.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {section.items.map((item) => {
                  const active = item.exact
                    ? location === item.href
                    : location.startsWith(item.href);
                  const Icon = item.icon;
                  return (
                    <SidebarMenuItem key={item.href}>
                      <SidebarMenuButton asChild isActive={active} tooltip={item.label}>
                        <Link href={item.href}>
                          <Icon />
                          <span>{item.label}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <ImpersonationExit />
          <TenantUserMenu />
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}

function TenantUserMenu() {
  const { data: me } = useTenantMe();
  const { isMobile } = useSidebar();
  const { setTheme } = useTheme();
  const qc = useQueryClient();
  const [, navigate] = useLocation();

  const initials = useMemo(() => {
    if (!me?.tenant.fullName) return '·';
    return me.tenant.fullName
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]!.toUpperCase())
      .join('');
  }, [me?.tenant.fullName]);

  async function logoutTenant() {
    await api('/api/app/auth/logout', { method: 'POST' });
    qc.clear();
    navigate('/app/login');
  }

  if (!me) return null;

  return (
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            size="lg"
            tooltip={me.tenant.fullName}
            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
          >
            <Avatar className="size-8 shrink-0 rounded-lg">
              <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
            </Avatar>
            <div className="grid min-w-0 flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
              <span className="truncate font-medium">{me.tenant.fullName}</span>
              <span className="truncate text-xs text-sidebar-foreground/70">
                {me.rucs[0]?.ruc ?? '—'}
              </span>
            </div>
            <ChevronsUpDown className="ml-auto size-4 shrink-0 group-data-[collapsible=icon]:hidden" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
          side={isMobile ? 'bottom' : 'right'}
          align="end"
          sideOffset={4}
        >
          <DropdownMenuLabel className="p-0 font-normal">
            <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
              <Avatar className="size-8 rounded-lg">
                <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{me.tenant.fullName}</span>
                {me.accountant && (
                  <span className="truncate text-xs text-muted-foreground">
                    Contador: {me.accountant.fullName}
                  </span>
                )}
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link href="/app/cuenta">
              <UserCircle />
              Mi cuenta
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link href="/app/notificaciones">
              <Bell />
              Notificaciones
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Contrast />
              Tema
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuItem onClick={() => setTheme('light')}>Claro</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme('dark')}>Oscuro</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme('system')}>Sistema</DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSeparator />
          <DropdownMenuItem variant="destructive" onClick={logoutTenant}>
            <LogOut />
            Cerrar sesión
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
}

/**
 * Item del sidebar (footer) que aparece SOLO durante impersonation: permite
 * al admin volver a su panel. Reemplaza la vieja barra superior de
 * impersonation (quedaba mal sobre el contenido). Estilo warning para que se
 * note que estás viendo el panel como otro usuario.
 */
function ImpersonationExit() {
  const { data: me } = useTenantMe();
  const qc = useQueryClient();
  const [, navigate] = useLocation();

  async function stop() {
    await api('/api/admin/stop-impersonate', { method: 'POST' });
    qc.clear();
    navigate('/admin/tenants');
  }

  if (!me?.impersonatedBy) return null;

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        onClick={stop}
        tooltip="Volver al admin"
        className="border border-warning/40 bg-warning/10 text-foreground hover:bg-warning/20 active:bg-warning/20"
      >
        <ArrowLeftRight className="text-warning" />
        <span className="truncate font-medium group-data-[collapsible=icon]:hidden">
          Volver al admin
        </span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

